import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { createContext, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { sessionChannel } from '@/auth/sessionChannel';
import { STORAGE_KEYS } from '@/constants/storageKeys';
import { darkTheme, theme as lightTheme, type ThemeMode } from '@/theme';

/** The user's stored *preference*: an explicit mode, or "follow the OS". */
export type ThemePreference = ThemeMode | 'system';

export interface ThemeModeContextValue {
  /** The resolved, currently-applied mode — never "system" (that's resolved to light/dark here). */
  mode: ThemeMode;
  /** The user's stored preference, including "system". */
  preference: ThemePreference;
  /** Set an explicit light/dark mode (used by the top-bar quick toggle). */
  setMode: (mode: ThemeMode) => void;
  /** Set any preference including "system" (used by the user menu's Theme submenu). */
  setPreference: (preference: ThemePreference) => void;
  /** Flip between light and dark explicitly. */
  toggleMode: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined);

const SYSTEM_QUERY = '(prefers-color-scheme: dark)';

function getInitialPreference(): ThemePreference {
  const stored = window.localStorage.getItem(STORAGE_KEYS.themeMode);
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  // No stored choice yet → follow the OS (docs/development/FrontendGuidelines.md §7.6).
  return 'system';
}

function resolveSystemMode(): ThemeMode {
  return window.matchMedia(SYSTEM_QUERY).matches ? 'dark' : 'light';
}

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(getInitialPreference);
  const [systemMode, setSystemMode] = useState<ThemeMode>(resolveSystemMode);

  // Track the OS preference so a "system" choice re-resolves live when the OS theme flips.
  useEffect(() => {
    const media = window.matchMedia(SYSTEM_QUERY);
    const onChange = () => setSystemMode(media.matches ? 'dark' : 'light');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const applyPreference = useCallback((next: ThemePreference, broadcast: boolean) => {
    setPreferenceState(next);
    window.localStorage.setItem(STORAGE_KEYS.themeMode, next);
    if (broadcast) sessionChannel.post('theme-changed', next);
  }, []);

  const setPreference = useCallback(
    (next: ThemePreference) => applyPreference(next, true),
    [applyPreference],
  );
  const setMode = useCallback((next: ThemeMode) => applyPreference(next, true), [applyPreference]);

  const mode = preference === 'system' ? systemMode : preference;

  const toggleMode = useCallback(() => {
    setMode(mode === 'light' ? 'dark' : 'light');
  }, [mode, setMode]);

  // Cross-tab theme sync (Task 3 "theme changed"): apply a peer tab's choice without re-broadcasting.
  useEffect(
    () =>
      sessionChannel.subscribe((event) => {
        if (event.type !== 'theme-changed') return;
        const next = event.payload;
        if (next === 'light' || next === 'dark' || next === 'system') {
          setPreferenceState(next);
          window.localStorage.setItem(STORAGE_KEYS.themeMode, next);
        }
      }),
    [],
  );

  const value = useMemo<ThemeModeContextValue>(
    () => ({ mode, preference, setMode, setPreference, toggleMode }),
    [mode, preference, setMode, setPreference, toggleMode],
  );
  const activeTheme = mode === 'dark' ? darkTheme : lightTheme;

  return (
    <ThemeModeContext.Provider value={value}>
      <MuiThemeProvider theme={activeTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
}
